import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import * as Sentry from "@sentry/react";
import { captureFirebaseError } from "../../config/sentry";

// Types
interface Activity {
  id: string;
  activityId?: string;
  userId: string;
  userName: string;
  timestamp?: { seconds: number };
  startTime?: any;
  endTime?: any;
  type: string;
  status: string;
  customerId?: string;
  expertId?: string;
  customerData?: any;
  expertData?: any;
  userData?: any;
  amount?: string;
  cost?: string;
  duration?: any;
  customerRating?: number;
  customerReview?: string;
  expertRating?: number;
  expertReview?: string;
  issueDetails?: string;
  hasIssue?: boolean;
  reportedBy?: string;
  reportedByName?: string;
  updatedAt?: any;
  createdAt?: any;
  expertOrganizationId?: string;
  [key: string]: any;
}

interface ActivityState {
  activities: Activity[];
  allActivities: Activity[];
  customerActivities: Activity[];
  expertActivities: Activity[];
  loading: boolean;
  error: string | null;
  // Real-time management
  activeListeners: { [key: string]: () => void };
  lastFetchTimestamp: { [key: string]: number };
  cacheExpiry: number;
  isRealTimeEnabled: boolean;
  connectionStatus: "connected" | "disconnected" | "connecting";
  syncInProgress: boolean;
}

const initialState: ActivityState = {
  activities: [],
  allActivities: [],
  customerActivities: [],
  expertActivities: [],
  loading: false,
  error: null,
  // Real-time management
  activeListeners: {},
  lastFetchTimestamp: {},
  cacheExpiry: 5 * 1000, // 5 seconds for real-time updates
  isRealTimeEnabled: true,
  connectionStatus: "disconnected",
  syncInProgress: false,
};

// Sentry transaction wrapper
const withSentryTransaction = async <T>(
  name: string,
  operation: string,
  func: () => Promise<T>
): Promise<T> => {
  const transaction = Sentry.startTransaction({
    name: `activity.${name}`,
    op: operation,
  });

  Sentry.configureScope((scope) => {
    scope.setSpan(transaction);
  });

  try {
    return await func();
  } catch (error) {
    captureFirebaseError(error as Error, {
      operation: name,
      transaction: transaction.toJSON(),
    });
    throw error;
  } finally {
    transaction.finish();
  }
};

// Helper function to batch fetch user data
async function batchFetchUserData(userIds: string[]): Promise<Record<string, any>> {
  try {
    const userData: Record<string, any> = {};

    if (userIds.length === 0) return userData;

    // Firestore 'in' operator has a limit of 10 items per query
    const batches = [];
    for (let i = 0; i < userIds.length; i += 10) {
      const batch = userIds.slice(i, i + 10);
      const usersRef = collection(db, "users");
      const userQuery = query(usersRef, where("__name__", "in", batch));
      batches.push(getDocs(userQuery));
    }

    // Execute all queries in parallel
    const results = await Promise.all(batches);

    // Process results
    results.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        userData[doc.id] = doc.data();
      });
    });

    return userData;
  } catch (error) {
    console.error("Error batch fetching user data:", error);
    return {};
  }
}

// Helper function to process activities data
async function processActivitiesData(docs: any[], role?: string): Promise<Activity[]> {
  try {
    if (docs.length === 0) return [];

    // Extract all unique user IDs
    const userIds = new Set<string>();
    const activities = docs.map((doc) => {
      const data = doc.data();
      const activity = {
        id: doc.id,
        activityId: doc.id,
        ...data,
      };

      if (activity.customerId) userIds.add(activity.customerId);
      if (activity.expertId) userIds.add(activity.expertId);

      return activity;
    });

    // Batch fetch all user data
    const userData = await batchFetchUserData(Array.from(userIds));

    // Enrich activities with user data
    const enrichedActivities: Activity[] = activities.map((activity) => {
      // Always fetch both customer and expert details
      if (activity.customerId && userData[activity.customerId]) {
        activity.customerData = {
          uid: activity.customerId,
          ...userData[activity.customerId],
          createdAt: userData[activity.customerId].createdAt
            ? new Date(userData[activity.customerId].createdAt.seconds * 1000)
            : null,
        };
      }

      if (activity.expertId && userData[activity.expertId]) {
        activity.expertData = {
          uid: activity.expertId,
          ...userData[activity.expertId],
          createdAt: userData[activity.expertId].createdAt
            ? new Date(userData[activity.expertId].createdAt.seconds * 1000)
            : null,
        };
      }

      // Set userData based on role
      if (role === "Expert" && activity.customerData) {
        activity.userData = activity.customerData;
      } else if (role === "Customer" && activity.expertData) {
        activity.userData = activity.expertData;
      }

      return activity;
    });

    // Sort by timestamp (newest first)
    enrichedActivities.sort((a, b) => {
      const getTimestamp = (activity: any) => {
        if (activity.updatedAt?.seconds) return activity.updatedAt.seconds;
        if (activity.endTime?.seconds) return activity.endTime.seconds;
        if (activity.startTime?.seconds) return activity.startTime.seconds;
        if (activity.timestamp?.seconds) return activity.timestamp.seconds;
        return 0;
      };

      return getTimestamp(b) - getTimestamp(a);
    });

    return enrichedActivities;
  } catch (error) {
    console.error("Error processing activities data:", error);
    return [];
  }
}

// Real-time fetch activities by role
export const fetchActivitiesByRole = createAsyncThunk(
  "activity/fetchActivitiesByRole",
  async (
    { role, userId }: { role: string; userId: string },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const state = getState() as { activity: ActivityState };
      const cacheKey = `activities_${role}_${userId}`;
      const now = Date.now();

      // Check cache
      const lastFetch = state.activity.lastFetchTimestamp[cacheKey] || 0;
      const isExpired = now - lastFetch > state.activity.cacheExpiry;

      // Get current data based on role
      const currentData = role === "Customer" 
        ? state.activity.customerActivities 
        : state.activity.expertActivities;

      if (currentData.length > 0 && !isExpired) {
        console.log(`Using cached ${role} activities`);
        return currentData;
      }

      // Clean up existing listener
      const existingListener = state.activity.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchActivitiesByRole",
        "data",
        async () => {
          // Determine field to match
          const fieldToMatch = role === "Expert" ? "expertId" : "customerId";

          // Query sessionActivity collection
          const sessionActivitiesRef = collection(db, "sessionActivity");
          const activitiesQuery = query(
            sessionActivitiesRef,
            where(fieldToMatch, "==", userId)
          );

          // Set up real-time listener
          const unsubscribe = onSnapshot(activitiesQuery, async (snapshot) => {
            if (!snapshot.metadata.fromCache) {
              console.log(`Real-time update detected for ${role} activities`);

              if (snapshot.empty) {
                const actionType = role === "Customer" 
                  ? "activity/updateCustomerActivitiesFromListener"
                  : "activity/updateExpertActivitiesFromListener";

                dispatch({
                  type: actionType,
                  payload: [],
                  meta: { cacheKey, timestamp: Date.now() },
                });
                return;
              }

              // Process activities with user data
              const activities = await processActivitiesData(snapshot.docs, role);

              const actionType = role === "Customer" 
                ? "activity/updateCustomerActivitiesFromListener"
                : "activity/updateExpertActivitiesFromListener";

              dispatch({
                type: actionType,
                payload: activities,
                meta: { cacheKey, timestamp: Date.now() },
              });

              // Also update allActivities
              dispatch({
                type: "activity/updateAllActivitiesFromListener",
                payload: activities,
                meta: { cacheKey: `allActivities_${role}`, timestamp: Date.now() },
              });
            }
          });

          // Store listener for cleanup
          dispatch({
            type: "activity/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Initial fetch
          const activitiesSnapshot = await getDocs(activitiesQuery);

          if (activitiesSnapshot.empty) {
            return [];
          }

          // Process activities with user data
          const activities = await processActivitiesData(activitiesSnapshot.docs, role);

          return activities;
        }
      );
    } catch (error) {
      console.error(`Failed to fetch ${role} activities:`, error);
      return rejectWithValue(`Failed to fetch ${role} activities: ${error.message}`);
    }
  }
);

// Fetch all activities with real-time updates
export const fetchAllActivities = createAsyncThunk(
  "activity/fetchAllActivities",
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { activity: ActivityState };
      const cacheKey = "allActivities";
      const now = Date.now();

      // Check cache
      const lastFetch = state.activity.lastFetchTimestamp[cacheKey] || 0;
      const isExpired = now - lastFetch > state.activity.cacheExpiry;

      if (state.activity.allActivities.length > 0 && !isExpired) {
        console.log("Using cached all activities");
        return state.activity.allActivities;
      }

      // Clean up existing listener
      const existingListener = state.activity.activeListeners[cacheKey];
      if (existingListener) {
        existingListener();
      }

      return await withSentryTransaction(
        "fetchAllActivities",
        "data",
        async () => {
          // Query all sessionActivity
          const sessionActivitiesRef = collection(db, "sessionActivity");

          // Set up real-time listener
          const unsubscribe = onSnapshot(sessionActivitiesRef, async (snapshot) => {
            if (!snapshot.metadata.fromCache) {
              console.log("Real-time update detected for all activities");

              if (snapshot.empty) {
                dispatch({
                  type: "activity/updateAllActivitiesFromListener",
                  payload: [],
                  meta: { cacheKey, timestamp: Date.now() },
                });
                return;
              }

              // Process activities with user data
              const activities = await processActivitiesData(snapshot.docs);

              dispatch({
                type: "activity/updateAllActivitiesFromListener",
                payload: activities,
                meta: { cacheKey, timestamp: Date.now() },
              });
            }
          });

          // Store listener for cleanup
          dispatch({
            type: "activity/setActiveListener",
            payload: { key: cacheKey, listener: unsubscribe },
          });

          // Initial fetch
          const activitiesSnapshot = await getDocs(sessionActivitiesRef);

          if (activitiesSnapshot.empty) {
            return [];
          }

          // Process activities with user data
          const activities = await processActivitiesData(activitiesSnapshot.docs);

          return activities;
        }
      );
    } catch (error) {
      console.error("Failed to fetch all activities:", error);
      return rejectWithValue(`Failed to fetch all activities: ${error.message}`);
    }
  }
);

// Update activity with immediate reflection
export const updateActivity = createAsyncThunk(
  "activity/updateActivity",
  async (
    { activityId, updateData }: { activityId: string; updateData: any },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Immediate UI update
      dispatch(immediateActivityUpdate({ activityId, updates: updateData }));

      // Update Firebase in background
      const sessionActivityRef = doc(db, "sessionActivity", activityId);
      await updateDoc(sessionActivityRef, {
        ...updateData,
        updatedAt: new Date(),
      });

      console.log("Activity updated successfully:", activityId);
      return { activityId, ...updateData };
    } catch (error) {
      console.error("Error updating activity:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Report issue with immediate reflection
export const reportActivityIssue = createAsyncThunk(
  "activity/reportActivityIssue",
  async (
    { 
      activityId, 
      issueDetails, 
      reportedBy, 
      reportedByName 
    }: { 
      activityId: string; 
      issueDetails: string; 
      reportedBy: string; 
      reportedByName: string; 
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const updateData = {
        issueDetails: issueDetails.trim(),
        hasIssue: true,
        status: "Issue reported",
        reportedBy,
        reportedByName,
      };

      // Immediate UI update
      dispatch(immediateActivityUpdate({ activityId, updates: updateData }));

      // Update Firebase in background
      const sessionActivityRef = doc(db, "sessionActivity", activityId);
      await updateDoc(sessionActivityRef, {
        ...updateData,
        updatedAt: new Date(),
      });

      console.log("Issue reported successfully:", activityId);
      return { activityId, ...updateData };
    } catch (error) {
      console.error("Error reporting issue:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Update rating and review with immediate reflection (enhanced for both customer and expert)
export const updateActivityRatingAndReview = createAsyncThunk(
  "activity/updateActivityRatingAndReview",
  async (
    { 
      activityId, 
      customerRating, 
      customerReview,
      expertRating,
      expertReview
    }: { 
      activityId: string; 
      customerRating?: number; 
      customerReview?: string;
      expertRating?: number;
      expertReview?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const updateData: any = {};
      
      // Add customer rating/review if provided
      if (customerRating !== undefined) updateData.customerRating = customerRating;
      if (customerReview !== undefined) updateData.customerReview = customerReview;
      
      // Add expert rating/review if provided
      if (expertRating !== undefined) updateData.expertRating = expertRating;
      if (expertReview !== undefined) updateData.expertReview = expertReview;

      // Immediate UI update
      dispatch(immediateActivityUpdate({ activityId, updates: updateData }));

      // Update Firebase in background
      const sessionActivityRef = doc(db, "sessionActivity", activityId);
      await updateDoc(sessionActivityRef, {
        ...updateData,
        updatedAt: new Date(),
      });

      console.log("Rating and review updated successfully:", activityId);
      return { activityId, ...updateData };
    } catch (error) {
      console.error("Error updating rating and review:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Action creators for immediate updates
export const immediateActivityUpdate = createAction<{
  activityId: string;
  updates: any;
}>("activity/immediateActivityUpdate");

export const forceRefreshActivities = createAction<{
  role?: string;
  userId?: string;
}>("activity/forceRefreshActivities");

// Activity slice
const activitySlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    // Real-time listener management
    setActiveListener: (state, action) => {
      const { key, listener } = action.payload;
      state.activeListeners[key] = listener;
    },
    cleanupListener: (state, action) => {
      const { key } = action.payload;
      if (state.activeListeners[key]) {
        state.activeListeners[key]();
        delete state.activeListeners[key];
      }
    },
    cleanupAllListeners: (state) => {
      Object.keys(state.activeListeners).forEach((key) => {
        if (state.activeListeners[key]) {
          state.activeListeners[key]();
        }
      });
      state.activeListeners = {};
    },
    // Real-time updates from listeners
    updateAllActivitiesFromListener: (state, action) => {
      state.allActivities = action.payload;
      state.loading = false;
      state.error = null;
      
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    updateCustomerActivitiesFromListener: (state, action) => {
      state.customerActivities = action.payload;
      state.activities = action.payload; // Backward compatibility
      state.loading = false;
      state.error = null;
      
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    updateExpertActivitiesFromListener: (state, action) => {
      state.expertActivities = action.payload;
      state.activities = action.payload; // Backward compatibility
      state.loading = false;
      state.error = null;
      
      if (action.meta) {
        state.lastFetchTimestamp[action.meta.cacheKey] = action.meta.timestamp;
      }
    },
    // Immediate updates for instant UI feedback
    immediateActivityUpdate: (state, action) => {
      const { activityId, updates } = action.payload;
      
      // Helper function to update activity in array
      const updateActivityInArray = (activities: Activity[]) => {
        return activities.map((activity) => {
          if (activity.id === activityId || activity.activityId === activityId) {
            return { ...activity, ...updates };
          }
          return activity;
        });
      };
      
      state.allActivities = updateActivityInArray(state.allActivities);
      state.customerActivities = updateActivityInArray(state.customerActivities);
      state.expertActivities = updateActivityInArray(state.expertActivities);
      state.activities = updateActivityInArray(state.activities);
    },
    // Connection status management
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    setSyncInProgress: (state, action) => {
      state.syncInProgress = action.payload;
    },
    setRealTimeEnabled: (state, action) => {
      state.isRealTimeEnabled = action.payload;
    },
    // Cache management
    clearCache: (state) => {
      state.lastFetchTimestamp = {};
    },
    removeExpiredCache: (state, action) => {
      const { key } = action.payload;
      delete state.lastFetchTimestamp[key];
    },
    // Force refresh
    forceRefreshActivities: (state, action) => {
      const { role, userId } = action.payload;
      if (role && userId) {
        const cacheKey = `activities_${role}_${userId}`;
        delete state.lastFetchTimestamp[cacheKey];
      } else {
        state.lastFetchTimestamp = {};
      }
    },
    // Reset state
    resetActivityState: (state) => {
      state.activities = [];
      state.allActivities = [];
      state.customerActivities = [];
      state.expertActivities = [];
      state.loading = false;
      state.error = null;
      state.lastFetchTimestamp = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchActivitiesByRole
      .addCase(fetchActivitiesByRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivitiesByRole.fulfilled, (state, action) => {
        const role = action.meta.arg.role;
        
        if (role === "Customer") {
          state.customerActivities = action.payload;
        } else if (role === "Expert") {
          state.expertActivities = action.payload;
        }
        
        state.activities = action.payload; // Backward compatibility
        state.loading = false;
        state.error = null;
        
        // Update cache timestamp
        const cacheKey = `activities_${role}_${action.meta.arg.userId}`;
        state.lastFetchTimestamp[cacheKey] = Date.now();
      })
      .addCase(fetchActivitiesByRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // fetchAllActivities
      .addCase(fetchAllActivities.pending, (state) => {
        if (state.allActivities.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchAllActivities.fulfilled, (state, action) => {
        state.allActivities = action.payload;
        state.loading = false;
        state.error = null;
        
        // Update cache timestamp
        state.lastFetchTimestamp["allActivities"] = Date.now();
      })
      .addCase(fetchAllActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // updateActivity
      .addCase(updateActivity.fulfilled, (state, action) => {
        // Activity already updated via immediateActivityUpdate
        console.log("Activity update completed in Firebase");
      })
      .addCase(updateActivity.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // reportActivityIssue
      .addCase(reportActivityIssue.fulfilled, (state, action) => {
        // Issue already updated via immediateActivityUpdate
        console.log("Issue report completed in Firebase");
      })
      .addCase(reportActivityIssue.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // updateActivityRatingAndReview
      .addCase(updateActivityRatingAndReview.fulfilled, (state, action) => {
        // Rating/review already updated via immediateActivityUpdate
        console.log("Rating and review update completed in Firebase");
      })
      .addCase(updateActivityRatingAndReview.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setActiveListener,
  cleanupListener,
  cleanupAllListeners,
  updateAllActivitiesFromListener,
  updateCustomerActivitiesFromListener,
  updateExpertActivitiesFromListener,
  setConnectionStatus,
  setSyncInProgress,
  setRealTimeEnabled,
  clearCache,
  removeExpiredCache,
  resetActivityState,
} = activitySlice.actions;

export default activitySlice.reducer;

// Cleanup function for component unmount
export const cleanupActivityListeners = () => (dispatch: any, getState: any) => {
  const state = getState();
  const activeListeners = state.activity.activeListeners;

  Object.keys(activeListeners).forEach((key) => {
    if (activeListeners[key]) {
      activeListeners[key]();
      dispatch(cleanupListener({ key }));
    }
  });
};

// Initialize real-time activity sync
export const initializeActivitySync = (role: string, userId: string) => (dispatch: any) => {
  console.log(`Initializing activity real-time sync for ${role}:${userId}`);
  
  // Fetch activities with real-time listeners
  dispatch(fetchActivitiesByRole({ role, userId }));
  
  console.log("Activity real-time sync initialized successfully");
};
